/**
 * ScrollIndicator - Vertical dot navigation for section jumping
 * Clean, minimal design with smooth active-state transitions
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { servicesDetail, teamSections } from '../../data/mock';

const PAGE_SECTIONS = {
  '/': [
    { id: 'hero', label: 'Welcome' },
    { id: 'what-we-do', label: 'What We Do' },
    { id: 'services', label: 'Services' },
    { id: 'partners', label: 'Partners' },
    { id: 'testimonials', label: 'Testimonials' },
  ],
  '/about': [
    { id: 'hero', label: 'Our Story' },
    { id: 'vision-mission', label: 'Vision & Mission' },
    { id: 'md-message', label: 'MD Message' },
    { id: 'policies', label: 'Policies' },
    { id: 'organization', label: 'Organization' },
    { id: 'certifications', label: 'Certifications' },
  ],
  '/services': servicesDetail.map((s) => ({ id: `service-${s.id}`, label: s.title })),
  '/projects': [{ id: 'projects-content', label: 'Projects' }],
  '/team': teamSections.map((s) => ({ id: `team-${s.id}`, label: s.title })),
  '/clients': [
    { id: 'clients-list', label: 'Clients' },
    { id: 'testimonials', label: 'Testimonials' },
  ],
  '/gallery': [
    { id: 'featured', label: 'Featured' },
    { id: 'gallery-grid', label: 'Gallery' },
  ],
  '/contact': [{ id: 'contact-form', label: 'Contact' }],
};

export function ScrollIndicator() {
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const location = useLocation();
  const ratiosRef = useRef(new Map());
  const observerRef = useRef(null);

  useEffect(() => {
    const pathSections = PAGE_SECTIONS[location.pathname] ?? [];
    if (pathSections.length === 0) {
      setSections([]);
      setActiveId(null);
      return;
    }

    setSections(pathSections);
    setActiveId(pathSections[0]?.id ?? null);
    ratiosRef.current.clear();

    const scheduleObserve = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.target.id) ratiosRef.current.set(e.target.id, e.intersectionRatio);
          });

          let bestId = null;
          let bestRatio = 0;
          pathSections.forEach(({ id }) => {
            const r = ratiosRef.current.get(id) ?? 0;
            if (r > bestRatio) {
              bestRatio = r;
              bestId = id;
            }
          });

          if (bestId) setActiveId(bestId);
        },
        { rootMargin: '-25% 0px -50% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      pathSections.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });

      observerRef.current = observer;
    };

    const t = setTimeout(scheduleObserve, 200);

    return () => {
      clearTimeout(t);
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [location.pathname]);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  if (sections.length === 0) return null;

  return (
    <nav
      id="scroll-indicator"
      aria-label="Page sections"
      className="hidden lg:flex flex-col items-center gap-3"
    >
      {sections.map((section) => {
        const isActive = activeId === section.id;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => scrollTo(section.id)}
            title={section.label}
            aria-label={`Go to ${section.label}`}
            aria-current={isActive ? 'true' : undefined}
            className="group relative flex items-center justify-center w-7 h-7 rounded-full transition-colors duration-200 hover:bg-[#8B7355]/8 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF8F2]"
          >
            <span
              className={`block rounded-full transition-all duration-300 ease-out ${
                isActive
                  ? 'w-2.5 h-2.5 bg-[#8B7355]'
                  : 'w-1.5 h-1.5 bg-[#8B7355]/35 group-hover:bg-[#8B7355]/55'
              }`}
            />
            <span className="absolute right-full mr-3 px-2.5 py-1.5 text-xs font-medium text-[#1A1A1A] bg-white/95 backdrop-blur-sm rounded-md shadow-md border border-[#E0DDD9]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap pointer-events-none z-50">
              {section.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
